import { STANDALONE_AND_INCLUDED_BASE_PLANS } from '@/common/constants';
import { ZOOM_MEETING_SUB_TYPES } from "@/plans/ZOOM_MEETING/common/utils";

// Base plan priority order for sorting
const BASE_PLAN_PRIORITY = {
  [ZOOM_MEETING_SUB_TYPES.PRO]: 1,
  [ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_PRO_PLUS]: 2,
  [ZOOM_MEETING_SUB_TYPES.BIZ]: 3,
  [ZOOM_MEETING_SUB_TYPES.BIZ_INR]: 4,
  [ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS]: 5,
};

/**
 * Get all base plans that include the current addon
 * @param {Object} getCurrentBasePlanInfo - current base plan info from vuex
 * @param {Array} getSelectedPlans - selected plans array from vuex
 * @param {Array} boughtPlans - bought plans from vuex
 * @param {String} currentAddonPlanType - current addon plan type
 * @returns {Array} - array of base plan objects that include this addon
 */
function getMatchingBasePlans(getCurrentBasePlanInfo, getSelectedPlans, boughtPlans, currentAddonPlanType) {
  const allBasePlansMap = new Map();
  
  // Collect from getCurrentBasePlanInfo
  const currentPlans = getCurrentBasePlanInfo?.plans || [];
  currentPlans.forEach(plan => {
    if (plan.sub_type) {
      allBasePlansMap.set(plan.sub_type, plan);
    }
  });
  
  // Collect from getSelectedPlans (filter only base plan types)
  const basePlanSubTypes = [
    ZOOM_MEETING_SUB_TYPES.PRO, 
    ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_PRO_PLUS, 
    ZOOM_MEETING_SUB_TYPES.BIZ, 
    ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS, 
    ZOOM_MEETING_SUB_TYPES.ZONE_EDU_PLUS, 
    ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP,
    ZOOM_MEETING_SUB_TYPES.EDU,
    ZOOM_MEETING_SUB_TYPES.EDUCATION
  ];
  (getSelectedPlans || []).forEach(plan => {
    if (plan.sub_type && basePlanSubTypes.includes(plan.sub_type)) {
      allBasePlansMap.set(plan.sub_type, plan);
    }
  });
  
  // Collect from boughtPlans (only base plan types: pro, pro plus, biz, biz plus)
  (boughtPlans || []).forEach(plan => {
    if (plan.sub_type && basePlanSubTypes.includes(plan.sub_type)) {
      allBasePlansMap.set(plan.sub_type, plan);
    }
  });
  
  // Convert Map to Array and filter by white list
  const allBasePlans = Array.from(allBasePlansMap.values());
  
  return allBasePlans.filter(basePlan => {
    const subType = basePlan.sub_type;
    const allowedAddons = STANDALONE_AND_INCLUDED_BASE_PLANS[subType] || [];
    return allowedAddons.includes(currentAddonPlanType);
  }).sort((a, b) => {
    // Sort by priority: pro < pro plus < biz < biz_inr < biz plus
    const priorityA = BASE_PLAN_PRIORITY[a.sub_type] || 999;
    const priorityB = BASE_PLAN_PRIORITY[b.sub_type] || 999;
    return priorityA - priorityB;
  });
}

/**
 * Check if should show addon info banner
 * @param {Object} getCurrentBasePlanInfo - current base plan info from vuex
 * @param {Array} getSelectedPlans - selected plans array from vuex
 * @param {Array} boughtPlans - bought plans from vuex
 * @param {String} currentAddonPlanType - current addon plan type (e.g., planTypes.ZOOM_SCHEDULER)
 * @param {Object} $context - vue context object
 * @returns {Boolean} - whether to show the banner
 */
export function shouldShowAddonInfoBanner(getCurrentBasePlanInfo, getSelectedPlans, boughtPlans, currentAddonPlanType, $context) {
  // Condition a: only buyNow
  if (!($context.is.buyNow)) {
    return false;
  }

  // Condition b: check if any base plan includes this addon
  const matchingPlans = getMatchingBasePlans(getCurrentBasePlanInfo, getSelectedPlans, boughtPlans, currentAddonPlanType);
  return matchingPlans.length > 0;
}

/**
 * Get formatted base plan names with i18n translation
 * Handles multiple base plans (max 2) that include the addon
 * @param {Object} getCurrentBasePlanInfo - current base plan info from vuex
 * @param {Array} getSelectedPlans - selected plans array from vuex
 * @param {Array} boughtPlans - bought plans from vuex
 * @param {String} currentAddonPlanType - current addon plan type
 * @param {Function} $t - vue i18n translate function
 * @returns {String} - formatted translated base plan name(s)
 */
export function getBasePlanName(getCurrentBasePlanInfo, getSelectedPlans, boughtPlans, currentAddonPlanType, $t) {
  const matchingPlans = getMatchingBasePlans(getCurrentBasePlanInfo, getSelectedPlans, boughtPlans, currentAddonPlanType);
  
  if (matchingPlans.length === 0) {
    return '';
  }

  // Get translated names
  const getTranslatedName = (plan) => {
    const planNameKey = plan.planNameKey;
    const typeNameKey = plan.typeNameKey;
    return (planNameKey && $t(planNameKey)) || (typeNameKey && $t(typeNameKey)) || '';
  };

  if (matchingPlans.length === 1) {
    return getTranslatedName(matchingPlans[0]);
  }

  // Handle 2 base plans
  if (matchingPlans.length === 2) {
    const basePlanName = getTranslatedName(matchingPlans[0]);
    const basePlanName2 = getTranslatedName(matchingPlans[1]);
    return $t('billing.addon.two_base_plan_name', { basePlanName, basePlanName2 });
  }

  // If more than 2 (edge case), just return the first one
  return getTranslatedName(matchingPlans[0]);
}

